import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Mark } from "@tiptap/core";
import {
  FaBold, FaItalic, FaUnderline, FaListUl, FaListOl, FaQuoteRight, FaCode, FaLink,
} from "react-icons/fa";

// @tiptap/extension-underline pakette yok; sunucunun izin verdiği <u> için küçük işaret.
const Underline = Mark.create({
  name: "underline",
  parseHTML() {
    return [{ tag: "u" }, { style: "text-decoration=underline" }];
  },
  renderHTML() {
    return ["u", 0];
  },
  addCommands() {
    return { toggleUnderline: () => ({ commands }) => commands.toggleMark(this.name) };
  },
  addKeyboardShortcuts() {
    return { "Mod-u": () => this.editor.commands.toggleUnderline() };
  },
});

const isImageFile = (f) => Boolean(f && f.type && f.type.startsWith("image/"));

// Yapıştırma/bırakmada yalnız saf görsel dosya varsa devralınır. Pano metin/HTML de
// taşıyorsa (Word/Excel hücreleri görsel önizlemesiyle gelir) Tiptap'e bırakılır ki metin kaybolmasın.
export function imageFilesToIntercept(dt) {
  if (!dt) return [];
  const images = Array.from(dt.files || []).filter(isImageFile);
  if (!images.length) return [];
  const types = Array.from(dt.types || []);
  const has = (t) => {
    if (types.includes(t)) return true;
    try {
      return Boolean(dt.getData && dt.getData(t));
    } catch (e) {
      return false;
    }
  };
  return has("text/html") || has("text/plain") ? [] : images;
}

const LINK_RE = /^(https?:\/\/|mailto:)/i;

// Görsel yalnızca data-attachment-id ile saklanır; src HTML'e yazılmaz.
// Düzenleme sırasında görüntü node view içinde (DOM'da) gösterilir.
const AttachmentImage = Image.extend({
  name: "image",
  addAttributes() {
    return {
      attachmentId: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-attachment-id"),
        renderHTML: (attrs) => (attrs.attachmentId ? { "data-attachment-id": attrs.attachmentId } : {}),
      },
    };
  },
  parseHTML() {
    return [{ tag: "img[data-attachment-id]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["img", HTMLAttributes];
  },
  addNodeView() {
    const { previews } = this.options;
    return ({ node }) => {
      const dom = document.createElement("img");
      dom.setAttribute("data-attachment-id", node.attrs.attachmentId || "");
      dom.className = "max-w-full rounded my-1";
      const id = node.attrs.attachmentId;
      const set = (u) => u && dom.setAttribute("src", u);
      if (previews.map.has(id)) set(previews.map.get(id));
      else if (previews.resolve) Promise.resolve(previews.resolve(id)).then(set).catch(() => {});
      return { dom };
    };
  },
});

function ToolButton({ active, onClick, title, children, disabled }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`px-2 py-1 rounded text-xs font-Medium min-w-[28px] ${
        active ? "bg-main text-white" : "text-gray-600 hover:bg-gray-100"
      } disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function RichTextEditor({ value = "", onChange, onUploadImage, resolveImage, editable = true, placeholder = "" }) {
  const previews = useRef({ map: new Map(), resolve: resolveImage });
  previews.current.resolve = resolveImage;
  const uploadRef = useRef(onUploadImage);
  uploadRef.current = onUploadImage;
  const [hint, setHint] = useState("");

  const insertImageFiles = (editor, images) => {
    if (!images.length) return false;
    if (!uploadRef.current) {
      setHint("Bu alanda görsel yüklenemez.");
      return true;
    }
    setHint("");
    images.forEach(async (file) => {
      try {
        const id = await uploadRef.current(file);
        if (!id) return;
        previews.current.map.set(id, URL.createObjectURL(file));
        editor.chain().focus().insertContent({ type: "image", attrs: { attachmentId: id } }).run();
      } catch (e) {
        setHint(e?.message || "Görsel yüklenemedi.");
      }
    });
    return true;
  };

  const editor = useEditor({
    extensions: [
      // Tiptap 3 StarterKit link/underline içeriyor; kendi ayarlı sürümlerimiz kullanılsın
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false, underline: false }),
      Underline,
      Link.configure({
        openOnClick: false,
        protocols: ["mailto"],
        isAllowedUri: (href) => LINK_RE.test(String(href || "")),
        shouldAutoLink: (href) => LINK_RE.test(String(href || "")),
      }),
      AttachmentImage.configure({ previews: previews.current }),
    ],
    content: value,
    editable,
    // Tiptap 3 varsayılanı false — araç çubuğundaki aktif durumlar güncellensin
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: ed }) => onChange && onChange(ed.isEmpty ? "" : ed.getHTML()),
    editorProps: {
      attributes: {
        class: "prose-sm min-h-[120px] px-3 py-2 outline-none text-sm text-gray-800 [&_h2]:text-lg [&_h2]:font-SemiBold [&_h3]:text-base [&_h3]:font-SemiBold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_code]:bg-gray-100 [&_code]:px-1 [&_pre]:bg-gray-100 [&_pre]:p-2 [&_a]:text-blue-600 [&_a]:underline",
        "data-placeholder": placeholder,
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": placeholder || "Metin",
      },
      handlePaste: (_view, event) => insertImageFiles(editorRef.current, imageFilesToIntercept(event.clipboardData)),
      handleDrop: (_view, event) => insertImageFiles(editorRef.current, imageFilesToIntercept(event.dataTransfer)),
    },
  });
  const editorRef = useRef(null);
  editorRef.current = editor;

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editor, editable]);

  // Dışarıdan değer değişirse (ör. sıfırlama) editörü eşitle
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? "" : editor.getHTML();
    if ((value || "") !== current) editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Bağlantı adresi (http, https veya mailto)", prev);
    if (url === null) return;
    if (url === "") return editor.chain().focus().unsetLink().run();
    if (!LINK_RE.test(url)) return setHint("Yalnız http, https veya mailto bağlantıları eklenebilir.");
    setHint("");
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  if (!editor) return null;
  const t = (name, attrs) => editor.isActive(name, attrs);

  return (
    <div className="rounded-lg border border-gray-300 bg-white focus-within:border-primary">
      {editable && (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 px-1.5 py-1">
          <ToolButton title="Kalın" active={t("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><FaBold /></ToolButton>
          <ToolButton title="İtalik" active={t("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><FaItalic /></ToolButton>
          <ToolButton title="Altı çizili" active={t("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><FaUnderline /></ToolButton>
          <ToolButton title="Başlık 2" active={t("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</ToolButton>
          <ToolButton title="Başlık 3" active={t("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>H3</ToolButton>
          <ToolButton title="Madde işaretli liste" active={t("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><FaListUl /></ToolButton>
          <ToolButton title="Numaralı liste" active={t("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><FaListOl /></ToolButton>
          <ToolButton title="Alıntı" active={t("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><FaQuoteRight /></ToolButton>
          <ToolButton title="Kod" active={t("code")} onClick={() => editor.chain().focus().toggleCode().run()}><FaCode /></ToolButton>
          <ToolButton title="Bağlantı" active={t("link")} onClick={setLink}><FaLink /></ToolButton>
        </div>
      )}
      <EditorContent editor={editor} />
      {hint && <p className="px-3 pb-2 text-xs text-red-600">{hint}</p>}
    </div>
  );
}

export default RichTextEditor;
